import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { useAuth } from "../AuthContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import * as OTPAuth from "otpauth";

const RECAPTCHA_SITE_KEY = "6LfSZPQsAAAAAAAVs-GwPeTbw-OdFpss3JA-rk5f";

// ── UI Taraflı Rate Limiting ─────────────────────────────────────────────────
const RL_KEY       = "login_attempts";
const MAX_ATTEMPTS = 10;
const LOCKOUT_MS   = 15 * 60 * 1000;

function getRLData() {
  try {
    return JSON.parse(localStorage.getItem(RL_KEY)) || { count: 0, lockedUntil: 0 };
  } catch { return { count: 0, lockedUntil: 0 }; }
}
function setRLData(data) {
  localStorage.setItem(RL_KEY, JSON.stringify(data));
}
function recordFailedAttempt() {
  const data = getRLData();
  const count = data.count + 1;
  const lockedUntil = count >= MAX_ATTEMPTS ? Date.now() + LOCKOUT_MS : data.lockedUntil;
  setRLData({ count, lockedUntil });
  return { count, lockedUntil };
}
function resetAttempts() {
  setRLData({ count: 0, lockedUntil: 0 });
}
function getLockoutRemaining() {
  const { lockedUntil } = getRLData();
  return Math.max(0, lockedUntil - Date.now());
}

// 2FA durumunu sessionStorage'dan temizle
function clear2FASession() {
  sessionStorage.removeItem("show2FA");
  sessionStorage.removeItem("twoFASecret");
}

export default function Login() {
  const [email, setEmail]         = useState("");
  const [password, setPassword]   = useState("");
  const [error, setError]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [twoFACode, setTwoFACode] = useState("");

  // ── DÜZELTME: show2FA ve secret sessionStorage'da saklanıyor ──────────────
  // Neden? Firebase login() başarılı olunca onAuthStateChanged tetiklenir →
  // PublicRoute yeniden render olur → Login bileşeni unmount/remount olur →
  // local useState(false) sıfırlanır → 2FA ekranı görünmez.
  // sessionStorage ile remount'tan sonra da doğru değeri okuyabiliriz.
  const [show2FA, setShow2FAState] = useState(
    () => sessionStorage.getItem("show2FA") === "true"
  );
  const [secret, setSecretState] = useState(
    () => sessionStorage.getItem("twoFASecret") || ""
  );

  function setShow2FA(val) {
    sessionStorage.setItem("show2FA", val ? "true" : "false");
    setShow2FAState(val);
  }
  function setSecret(val) {
    sessionStorage.setItem("twoFASecret", val);
    setSecretState(val);
  }
  // ─────────────────────────────────────────────────────────────────────────

  // ── DÜZELTME: captchaToken 2FA ekranında ARANMAZ ──────────────────────────
  // Orijinal kodda captchaToken null olduğu için buton disabled kalıyordu.
  // 2FA ekranı ayrı bir akış — captcha sadece ilk giriş adımında gerekli.
  const [captchaToken, setCaptchaToken] = useState(null);
  // ─────────────────────────────────────────────────────────────────────────

  // Rate limiting state
  const [lockoutRemaining, setLockoutRemaining] = useState(getLockoutRemaining());
  const [attemptCount, setAttemptCount]         = useState(getRLData().count);
  const lockoutTimerRef = useRef(null);

  const recaptchaRef = useRef(null);
  const { login, setAwaitingTwoFA } = useAuth();
  const navigate = useNavigate();

  // Kilit geri sayım sayacı
  useEffect(() => {
    if (lockoutRemaining <= 0) return;
    lockoutTimerRef.current = setInterval(() => {
      const rem = getLockoutRemaining();
      setLockoutRemaining(rem);
      if (rem <= 0) {
        clearInterval(lockoutTimerRef.current);
        resetAttempts();
        setAttemptCount(0);
        setError("");
      }
    }, 1000);
    return () => clearInterval(lockoutTimerRef.current);
  }, [lockoutRemaining]);

  // ── DÜZELTME: show2FA=true ama awaitingTwoFA=true iken PublicRoute
  // bileşeni Login'i yeniden render eder. sessionStorage'dan show2FA=true
  // okunur — bu doğru. Ama awaitingTwoFA=true olduğu için PublicRoute
  // redirect YAPMIYOR (currentUser && !awaitingTwoFA koşulu sağlanmıyor).
  // Bu zaten doğru çalışıyor. Ek bir effect'e gerek yok.
  // ─────────────────────────────────────────────────────────────────────────

  const formatTime = (ms) => {
    const secs = Math.ceil(ms / 1000);
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}:${String(s).padStart(2, "0")}` : `${s}s`;
  };

  async function handleSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    setError("");

    const remaining = getLockoutRemaining();
    if (remaining > 0) {
      setError(`Çok fazla başarısız deneme. Lütfen ${formatTime(remaining)} bekleyin.`);
      return;
    }

    if (!captchaToken) {
      setError("Lütfen önce robot olmadığını doğrula.");
      return;
    }

    setLoading(true);
    // awaitingTwoFA'yı login()'den ÖNCE true yap — onAuthStateChanged
    // login() çözülür çözülmez tetiklenebilir, guard o an aktif olmalı
    setAwaitingTwoFA(true);

    try {
      const result = await login(email, password);

      // E-posta doğrulanmamışsa girişi engelle
      if (!result.user.emailVerified) {
        await result.user.reload();
        if (!result.user.emailVerified) {
          setAwaitingTwoFA(false);
          clear2FASession();
          recaptchaRef.current?.reset();
          setCaptchaToken(null);
          setError("E-posta adresin henüz doğrulanmamış. Gelen kutunu kontrol et.");
          setLoading(false);
          return;
        }
      }

      const ref  = doc(db, "users", result.user.uid);
      const snap = await getDoc(ref);

      resetAttempts();
      setAttemptCount(0);

      if (snap.exists() && snap.data().twoFAEnabled) {
        // 2FA gerekiyor — secret'ı sessionStorage'a yaz, ekranı aç
        setSecret(snap.data().twoFASecret);
        setShow2FA(true);
        setLoading(false);
        // awaitingTwoFA true kalmaya devam eder — kullanıcı kodu girene kadar
      } else {
        // 2FA yok — direkt geç
        setAwaitingTwoFA(false);
        clear2FASession();
        navigate("/dashboard");
      }
    } catch (err) {
      setAwaitingTwoFA(false);
      clear2FASession();
      recaptchaRef.current?.reset();
      setCaptchaToken(null);

      const { count, lockedUntil } = recordFailedAttempt();
      setAttemptCount(count);

      if (lockedUntil > Date.now()) {
        setLockoutRemaining(getLockoutRemaining());
        setError(`${MAX_ATTEMPTS} başarısız deneme. Hesap ${formatTime(LOCKOUT_MS)} süreyle kilitlendi.`);
        setLoading(false);
        return;
      }

      const msgs = {
        "auth/user-not-found":    "Bu e-posta ile kayıtlı kullanıcı bulunamadı.",
        "auth/wrong-password":    "Şifre yanlış, tekrar deneyin.",
        "auth/invalid-email":     "Geçersiz e-posta adresi.",
        "auth/too-many-requests": "Çok fazla deneme. Lütfen bekleyin.",
        "auth/invalid-credential":"E-posta veya şifre hatalı.",
      };
      const rem  = MAX_ATTEMPTS - count;
      const base = msgs[err.code] || "Giriş yapılamadı.";
      setError(rem > 0 ? `${base} (${rem} deneme hakkı kaldı)` : base);
      setLoading(false);
    }
  }

  // ── 2FA doğrulama ────────────────────────────────────────────────────────
  // DÜZELTME: verify2FA artık captchaToken veya başka bir şeye bağımlı değil.
  // Sadece secret ve twoFACode kullanır.
  function verify2FA() {
    if (!twoFACode || twoFACode.length !== 6) {
      setError("Lütfen 6 haneli kodu girin.");
      return;
    }
    const totp  = new OTPAuth.TOTP({ secret, digits: 6 });
    const delta = totp.validate({ token: twoFACode, window: 1 });
    if (delta !== null) {
      setAwaitingTwoFA(false);
      clear2FASession();
      navigate("/dashboard");
    } else {
      setError("Kod yanlış, tekrar dene.");
    }
  }
  // ─────────────────────────────────────────────────────────────────────────

  // 2FA ekranı
  if (show2FA) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="auth-logo">🔐</div>
            <h1>2FA Doğrulama</h1>
            <p>Authenticator uygulamanındaki 6 haneli kodu gir</p>
          </div>
          {error && <div className="auth-error">{error}</div>}
          <div className="auth-form">
            <div className="form-group">
              <label>Doğrulama Kodu</label>
              <input
                type="text"
                placeholder="000000"
                maxLength={6}
                value={twoFACode}
                onChange={e => setTwoFACode(e.target.value.replace(/\D/g, ""))}
                onKeyDown={e => e.key === "Enter" && verify2FA()}
                autoFocus
                style={{ letterSpacing:"0.2em", fontFamily:"monospace", fontSize:"1.2rem", textAlign:"center" }}
              />
            </div>
            {/* DÜZELTME: disabled={false} — 2FA butonunu captcha'ya bağlamak GEREKMİYOR */}
            <button className="auth-btn" onClick={verify2FA}>
              Doğrula ve Giriş Yap
            </button>
            <button
              className="auth-btn"
              style={{ marginTop:"0.5rem", background:"transparent", border:"1px solid rgba(255,255,255,0.2)", fontSize:"0.85rem" }}
              onClick={() => {
                setShow2FA(false);
                sessionStorage.setItem("show2FA", "false");
                setAwaitingTwoFA(false);
                sessionStorage.setItem("awaitingTwoFA", "false");
                setTwoFACode("");
                setError("");
              }}
            >
              ← Geri Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isLocked = lockoutRemaining > 0;

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">⬡</div>
          <h1>Tekrar hoşgeldin</h1>
          <p>Hesabına giriş yap</p>
        </div>

        {isLocked && (
          <div className="auth-error" style={{ background:"rgba(239,68,68,0.12)",border:"1px solid rgba(239,68,68,0.4)",borderRadius:"10px",padding:"0.85rem 1rem",marginBottom:"1rem",textAlign:"center" }}>
            <div style={{ fontSize:"1.5rem",marginBottom:"0.3rem" }}>🔒</div>
            <strong>Hesap Geçici Olarak Kilitlendi</strong>
            <div style={{ marginTop:"0.35rem",fontSize:"0.85rem" }}>
              Kalan süre: <strong style={{ fontFamily:"monospace",color:"#fbbf24" }}>{formatTime(lockoutRemaining)}</strong>
            </div>
          </div>
        )}

        {!isLocked && attemptCount > 0 && (
          <div style={{ background:"rgba(251,191,36,0.08)",border:"1px solid rgba(251,191,36,0.3)",borderRadius:"10px",padding:"0.65rem 1rem",marginBottom:"0.75rem",fontSize:"0.82rem",color:"#fde68a",textAlign:"center" }}>
            ⚠️ {MAX_ATTEMPTS - attemptCount} başarısız deneme hakkın kaldı
          </div>
        )}

        {error && !isLocked && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label>E-posta</label>
            <input type="email" placeholder="ornek@mail.com" value={email}
              onChange={e => setEmail(e.target.value)} required disabled={isLocked} />
          </div>
          <div className="form-group">
            <label>Şifre</label>
            <input type="password" placeholder="••••••••" value={password}
              onChange={e => setPassword(e.target.value)} required disabled={isLocked} />
          </div>

          <div style={{ display:"flex", justifyContent:"center", margin:"0.75rem 0" }}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={token => setCaptchaToken(token)}
              onExpired={() => setCaptchaToken(null)}
              theme="dark"
            />
          </div>

          <button type="submit" className="auth-btn" disabled={loading || !captchaToken || isLocked}>
            {loading ? <span className="spinner" /> : isLocked ? `🔒 ${formatTime(lockoutRemaining)}` : "Giriş Yap"}
          </button>
        </form>

        <p className="auth-switch">
          Hesabın yok mu? <Link to="/register">Kayıt ol</Link>
        </p>
      </div>
    </div>
  );
}
